import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { ChoiceGroup } from "./ChoiceGroup";
import { PhotoUpload } from "./PhotoUpload";
import { COLORS, DOOR_GLASS, GLAZING, HOME_TYPES, OPERATIONS, PRODUCTS, QUANTITIES, ROOF_SITUATIONS, TIMELINES, isDoor, postcodeError, quantityLabel, usesColor, usesGlass, usesQuantity } from "./wizardOptions";
import { imageUrl } from "../../lib/api";

const Optional = () => <span className="font-normal text-slate-500">(optioneel)</span>;

export const Field = ({ id, label, error, required, note, children }) => (
  <div>
    <Label htmlFor={id} className="text-[14px] font-semibold text-ink">
      {label} {required ? <span className="text-maas" aria-hidden="true">*</span> : note !== undefined ? <span className="font-normal text-slate-500">{note}</span> : <Optional />}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && <p className="mt-1.5 text-[13px] font-medium text-red-600" role="alert" data-testid={`wizard-error-${id}`}>{error}</p>}
  </div>
);

export const inputCls = (err) => `h-12 rounded-xl text-[15px] ${err ? "border-red-400 focus-visible:ring-red-300" : "border-slate-300"}`;

const SelectedDoor = ({ door, form, onRemove }) => (
  <div className="mt-5 flex items-center gap-4 rounded-2xl border border-maas/30 bg-maas-soft/60 p-3 pr-4" data-testid="wizard-selected-door">
    <img src={imageUrl(door.image)} alt="" className="h-20 w-16 shrink-0 rounded-lg object-cover" />
    <div className="min-w-0 flex-1">
      <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-maas-deep">Gekozen deur</p>
      <p className="mt-0.5 font-semibold text-ink" data-testid="wizard-selected-door-title">{door.title}</p>
      <p className="text-[13px] text-slate-500" data-testid="wizard-selected-door-meta">
        {door.material === "kunststof" ? "Kunststof" : "Aluminium"}{door.model_name ? ` · ${door.model_name}` : ""}
        {form.color ? ` · ${form.color}` : ""}{form.door_glass_type ? ` · ${form.door_glass_type}` : ""}
        {!form.color && !form.door_glass_type ? " · kleur en glas kiest u in stap 3" : ""}
      </p>
    </div>
    <button type="button" onClick={onRemove} aria-label="Deur verwijderen" data-testid="wizard-remove-door"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-ink"><X className="h-4 w-4" /></button>
  </div>
);

export const ProductStep = ({ form, set, door }) => (
  <>
    <ChoiceGroup name="product" options={PRODUCTS} value={form.product} onChange={(v) => set("product", v)} cols="grid-cols-1 sm:grid-cols-2" large />
    {door && <SelectedDoor door={door} form={form} onRemove={() => set("door_model", null)} />}
  </>
);

export const WoningStep = ({ form, set }) => (
  <div className="space-y-7">
    <ChoiceGroup name="woningtype" label="Type woning" options={HOME_TYPES} value={form.home_type} onChange={(v) => set("home_type", v)} />
    {usesQuantity(form.product) && <ChoiceGroup name="aantal" label={quantityLabel(form.product)} options={QUANTITIES} value={form.quantity} onChange={(v) => set("quantity", v)} />}
    <div className="grid gap-4 sm:grid-cols-2">
      <Field id="postcode" label="Postcode" error={postcodeError(form.postcode)}>
        <Input id="postcode" value={form.postcode} maxLength={7} autoComplete="postal-code" placeholder="3142 AB" data-testid="wizard-postcode"
          onChange={(e) => set("postcode", e.target.value.toUpperCase())} className={inputCls(postcodeError(form.postcode))} />
      </Field>
      <Field id="woonplaats" label="Woonplaats">
        <Input id="woonplaats" value={form.woonplaats} maxLength={80} autoComplete="address-level2" placeholder="Maassluis" data-testid="wizard-woonplaats"
          onChange={(e) => set("woonplaats", e.target.value)} className={inputCls()} />
      </Field>
    </div>
  </div>
);

const asOptions = (labels) => labels.map((l) => ({ id: l, label: l }));

export const WensenStep = ({ form, set, door, systemOptions = [] }) => {
  const colorOptions = door?.colorLabels.length ? [...asOptions(door.colorLabels), { id: "Advies gewenst", label: "Advies gewenst" }] : COLORS;
  const glassTypes = isDoor(form.product) && door && form.door_glass === "ja" ? door.glassLabels : [];
  return (
    <div className="space-y-7">
      {form.product === "velux_dakramen" && <ChoiceGroup name="dakraam" label="Wat wilt u laten doen?" options={ROOF_SITUATIONS} value={form.roof_situation} onChange={(v) => set("roof_situation", v)} cols="grid-cols-1 sm:grid-cols-2" />}
      {form.product === "rolluiken" && <ChoiceGroup name="bediening" label="Gewenste bediening" options={OPERATIONS} value={form.operation} onChange={(v) => set("operation", v)} cols="grid-cols-1 sm:grid-cols-2" />}
      {usesColor(form.product) && <ChoiceGroup name="kleur" label={door ? "Kleur van de deur" : "Gewenste kleur"} options={colorOptions} value={form.color} onChange={(v) => set("color", v)} />}
      {usesGlass(form.product) && <ChoiceGroup name="glas" label="Glas" options={GLAZING} value={form.glazing} onChange={(v) => set("glazing", v)} />}
      {isDoor(form.product) && <ChoiceGroup name="deurglas" label="Glas in de deur" options={DOOR_GLASS} value={form.door_glass} onChange={(v) => set("door_glass", v)} />}
      {glassTypes.length > 0 && <ChoiceGroup name="glasuitvoering" label="Glasuitvoering" options={asOptions(glassTypes)} value={form.door_glass_type} onChange={(v) => set("door_glass_type", v)} />}
      {systemOptions.length > 0 && (
        <ChoiceGroup name="profielsysteem" label="Profielsysteem" options={[...systemOptions, { id: "advies", label: "Geen voorkeur / advies" }]}
          value={form.profile_system} onChange={(v) => set("profile_system", v)} cols="grid-cols-1 sm:grid-cols-2" />
      )}
      <ChoiceGroup name="planning" label="Wanneer wilt u starten?" options={TIMELINES} value={form.timeline} onChange={(v) => set("timeline", v)} cols="grid-cols-1 sm:grid-cols-2" />
    </div>
  );
};

export const FotoStep = ({ form, set, uploads, setUploads }) => (
  <div className="space-y-6">
    <PhotoUpload uploads={uploads} setUploads={setUploads} />
    <Field id="afmetingen" label="Globale afmetingen">
      <Textarea id="afmetingen" rows={2} maxLength={300} value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} data-testid="wizard-afmetingen"
        placeholder="Bijv. pui ca. 3,5 x 2,2 m en vier ramen van ca. 1,2 x 1,5 m" className="rounded-xl border-slate-300 text-[15px]" />
    </Field>
    <Field id="opmerkingen" label="Opmerkingen of vragen">
      <Textarea id="opmerkingen" rows={3} maxLength={2000} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wizard-opmerkingen"
        placeholder="Bijv. ik wil de kozijnen aan de voorzijde vervangen en twijfel tussen kunststof en aluminium." className="rounded-xl border-slate-300 text-[15px]" />
    </Field>
    <p className="text-[13px] text-slate-500">Geen foto's of maten bij de hand? Geen probleem, u kunt deze stap gewoon overslaan.</p>
  </div>
);

export const ContactStep = ({ form, set, errors }) => (
  <div className="space-y-4">
    <Field id="naam" label="Naam" required error={errors.naam}>
      <Input id="naam" value={form.naam} maxLength={100} autoComplete="name" placeholder="Voor- en achternaam" data-testid="wizard-naam"
        onChange={(e) => set("naam", e.target.value)} className={inputCls(errors.naam)} aria-invalid={!!errors.naam} />
    </Field>
    <div className="grid gap-4 sm:grid-cols-2">
      <Field id="email" label="E-mailadres" required error={errors.email}>
        <Input id="email" type="email" inputMode="email" value={form.email} maxLength={120} autoComplete="email" placeholder="naam@voorbeeld.nl" data-testid="wizard-email"
          onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} aria-invalid={!!errors.email} />
      </Field>
      <Field id="telefoon" label="Telefoonnummer" required error={errors.telefoon}>
        <Input id="telefoon" type="tel" inputMode="tel" value={form.telefoon} maxLength={20} autoComplete="tel" placeholder="06 12345678" data-testid="wizard-telefoon"
          onChange={(e) => set("telefoon", e.target.value)} className={inputCls(errors.telefoon)} aria-invalid={!!errors.telefoon} />
      </Field>
    </div>
    <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website}
      onChange={(e) => set("website", e.target.value)} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
    <p className="pt-1 text-[13px] leading-relaxed text-slate-500">
      Wij gebruiken uw gegevens alleen om uw aanvraag te beantwoorden. Door te versturen gaat u akkoord met onze{" "}
      <Link to="/privacy" className="font-medium text-maas underline underline-offset-2" target="_blank">privacyverklaring</Link>.
    </p>
  </div>
);
